import * as React from 'react';

import {
  AuditDrawer,
  type AuditEntry,
  type AuditEvidenceRef,
} from './audit-drawer';
import {
  CommentsAndAttachmentsPanel,
  type CommentEntry,
  type AttachmentEntry,
} from './comments-and-attachments-panel';

/**
 * Shape of the audit envelope as returned by the cross-module
 * `/audit/{entityKind}/{entityId}` endpoint. Kept structurally
 * compatible with `useEntityAuditTrail` from `@tyndo/hooks` so the
 * caller can pass `audit.data` directly without remapping.
 */
export interface CollaborationAuditEntry {
  id: string;
  occurredAt: string;
  actorId?: string | null;
  actorRole?: string | null;
  action: string;
  previousState?: string | null;
  nextState?: string | null;
  reason?: string | null;
  correlationId?: string | null;
  evidenceRefsJson?: string | null;
}

export interface CollaborationCommentRecord {
  id: string;
  parentCommentId?: string | null;
  authorDisplayName: string;
  body: string;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CollaborationAttachmentRecord {
  id: string;
  fileName: string;
  contentType?: string | null;
  sizeBytes?: number | null;
  uploadedBy?: string | null;
  uploadedAt: string;
}

export interface EntityCollaborationRailProps {
  /** Human-readable record label (e.g. "PO 4501232142"). */
  entityLabel: string;
  /** Wire envelopes — pass directly from the `@tyndo/hooks` queries. */
  auditEntries: CollaborationAuditEntry[];
  comments: CollaborationCommentRecord[];
  attachments: CollaborationAttachmentRecord[];
  /** Drawer controls. */
  auditOpen: boolean;
  onAuditOpenChange: (open: boolean) => void;
  /** Loading flags from the upstream queries. */
  auditLoading?: boolean;
  commentsLoading?: boolean;
  attachmentsLoading?: boolean;
  /** Mutations — wired to the comments/attachments API hooks. */
  onAddComment: (body: string) => Promise<void> | void;
  onAddAttachment?: (file: File) => Promise<void> | void;
  onDeleteComment?: (id: string) => Promise<void> | void;
  onDeleteAttachment?: (id: string) => Promise<void> | void;
  /** Build a download URL for an attachment id (gateway streams blob). */
  buildAttachmentHref?: (id: string) => string;
  /**
   * Surface emoji reactions on each comment (Slack/Teams "react" baseline),
   * wired to `/comments/{id}/reactions`. On by default — the rail's comments
   * are real entity-comment rows the reaction endpoints accept. Pass `false`
   * to suppress the affordance for a read-only rail.
   */
  reactionsEnabled?: boolean;
  /** The current operator's user id, used to highlight the caller's own reactions. */
  currentUserId?: string;
  /** Optional className for the panel section. */
  panelClassName?: string;
}

function parseEvidenceRefs(json?: string | null): AuditEvidenceRef[] | undefined {
  if (!json) return undefined;
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!Array.isArray(parsed)) return undefined;
    const refs: AuditEvidenceRef[] = [];
    for (const raw of parsed) {
      if (!raw || typeof raw !== 'object') continue;
      const obj = raw as Record<string, unknown>;
      const kind = typeof obj.kind === 'string' ? obj.kind : undefined;
      const id = typeof obj.id === 'string' ? obj.id : undefined;
      if (!kind || !id) continue;
      const ref: AuditEvidenceRef = { kind, id };
      if (typeof obj.description === 'string') ref.description = obj.description;
      if (typeof obj.url === 'string') ref.url = obj.url;
      refs.push(ref);
    }
    return refs;
  } catch {
    return undefined;
  }
}

/**
 * Convert the wire envelope (`occurredAt`, `action`, `actorId`) into
 * the `AuditEntry` shape the drawer expects. Keeps the mapping in one
 * place so each consuming view doesn't restate the field rename.
 */
export function toAuditEntries(
  entries: CollaborationAuditEntry[],
): AuditEntry[] {
  return entries.map((e) => ({
    id: e.id,
    actor: e.actorId ?? 'system',
    timestamp: e.occurredAt,
    transition: e.action,
    role: e.actorRole ?? undefined,
    previousState: e.previousState ?? undefined,
    nextState: e.nextState ?? undefined,
    reason: e.reason ?? undefined,
    correlationId: e.correlationId ?? undefined,
    evidenceRefs: parseEvidenceRefs(e.evidenceRefsJson),
  }));
}

export function toCommentEntries(
  records: CollaborationCommentRecord[],
): CommentEntry[] {
  return records.map((c) => ({
    id: c.id,
    author: c.authorDisplayName,
    postedAt: c.createdAt,
    body: c.body,
    replyToId: c.parentCommentId ?? undefined,
  }));
}

export function toAttachmentEntries(
  records: CollaborationAttachmentRecord[],
  buildHref?: (id: string) => string,
): AttachmentEntry[] {
  return records.map((a) => ({
    id: a.id,
    fileName: a.fileName,
    sizeBytes: a.sizeBytes ?? undefined,
    uploadedAt: a.uploadedAt,
    uploadedBy: a.uploadedBy ?? undefined,
    contentType: a.contentType ?? undefined,
    href: buildHref ? buildHref(a.id) : undefined,
  }));
}

/**
 * One-shot rail that combines the audit drawer + the comments and
 * attachments panel. Lives at the bottom of every regulated /
 * financial / operational detail workbench (PLM, EBR, Batch, Quality,
 * Compliance, Procurement, Maintenance, Sales, CRM, Finance).
 *
 * The drawer is controlled — the caller renders the "Audit trail"
 * button + toggles `auditOpen`. The panel is uncontrolled and renders
 * inline.
 */
export function EntityCollaborationRail({
  entityLabel,
  auditEntries,
  comments,
  attachments,
  auditOpen,
  onAuditOpenChange,
  auditLoading = false,
  commentsLoading = false,
  attachmentsLoading = false,
  onAddComment,
  onAddAttachment,
  onDeleteComment,
  onDeleteAttachment,
  buildAttachmentHref,
  reactionsEnabled = true,
  currentUserId,
  panelClassName,
}: EntityCollaborationRailProps) {
  const auditList = React.useMemo(
    () => toAuditEntries(auditEntries),
    [auditEntries],
  );
  const commentList = React.useMemo(
    () => toCommentEntries(comments),
    [comments],
  );
  const attachmentList = React.useMemo(
    () => toAttachmentEntries(attachments, buildAttachmentHref),
    [attachments, buildAttachmentHref],
  );

  return (
    <>
      <AuditDrawer
        open={auditOpen}
        onOpenChange={onAuditOpenChange}
        title="Audit trail"
        subtitle={entityLabel}
        entries={auditList}
        loading={auditLoading}
      />
      <CommentsAndAttachmentsPanel
        className={panelClassName}
        recordLabel={entityLabel}
        comments={commentList}
        attachments={attachmentList}
        onAddComment={onAddComment}
        onUpload={onAddAttachment}
        onDeleteComment={onDeleteComment}
        onDeleteAttachment={onDeleteAttachment}
        loading={commentsLoading || attachmentsLoading}
        reactionsEnabled={reactionsEnabled}
        currentUserId={currentUserId}
      />
    </>
  );
}

EntityCollaborationRail.displayName = 'EntityCollaborationRail';
